/**
 * FieldMediaGallery (Inventory Screens Step 5.3 Phase 2 — D-5.3-1) — the
 * field modal's media section: 3-col thumb grid (Bob's intent) + Camera /
 * Upload capture affordances. NET-NEW, deliberately NOT a PhotoGalleryPanel
 * fork. EDIT MODE ONLY — the host renders it only for real records
 * (create-mode media is Phase 3, D.4 seam).
 *
 * Sources: the record row's media[] — Load entries ({media_id, photo_type,
 * blob_path, content_hash, uploaded_at, sas_url}) AND the optimistic pending
 * entries this component appends ({outbox_id, pending:true, object_url,
 * photo_type, file_ext}, D-5.3-4). Thumb src fallback chain:
 * sas_url → blob_url (stamped at reconcile from the upload 201) → object_url.
 *
 * Capture flow (Phase 2: COMPRESSED AT CAPTURE — max edge 1600px, JPEG q0.70
 * via the field-local media/compressImage. Still never routes through
 * offlineEngine/ensureJpeg, which is unexported, does not resize, and belongs
 * to the prod disposal path. compressImage FAILS OPEN: on any failure the
 * original bytes are queued unchanged and the fail-open is logged, so HEIC that
 * the browser cannot decode still rides as HEIC):
 *   file select → compress → base64 → enqueueMediaAdd (photo_type HARDCODED
 *   'tool_photo', D-5.3-5 no type prompt) → append optimistic entry to the
 *   row's media[] (fieldDb direct write — the 5.2b handleCreate precedent)
 *   → onRecordChange(row) for the instant thumb.
 *   Enqueue-BEFORE-row-write: a queue item without a row entry self-heals
 *   via re-Load; a row entry without a queue item would be a permanent
 *   ghost pending.
 *
 * Camera = <input accept="image/*" capture="environment">, Upload =
 * <input accept="image/*" multiple> — Bob's fc2/fg2 hidden-input pattern,
 * value reset after each pick so re-selecting the same file re-fires.
 * Non-image files are skipped (Bob's guard).
 */

import React, { useRef, useState } from 'react';
import { fieldDb } from '../db/fieldDb';
import { ensureOpen } from '../db/fieldStore';
import { enqueueMediaAdd } from '../sync/fieldOutbox';
import { isPendingRecord } from '../sync/pendingId';
import { compressImage } from '../media/compressImage';
import './FieldMediaGallery.css';

// Server VALID_EXTS = {jpg, jpeg, png, heic, heif}; camera Files sometimes
// carry no useful name — fall back through the mime type, then jpg.
const MIME_EXT = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/heic': 'heic',
  'image/heif': 'heif',
};

function extOf(file) {
  const fromName = (file.name || '').includes('.')
    ? file.name.split('.').pop().toLowerCase()
    : null;
  return fromName || MIME_EXT[(file.type || '').toLowerCase()] || 'jpg';
}

// Accept-side allowlist — byte-parity with upload_media's server-side
// VALID_EXTS. Do not widen independently: a client accept the server
// rejects is a queued photo that can never drain.
const IMAGE_EXTS = new Set(['jpg', 'jpeg', 'png', 'heic', 'heif']);

/**
 * Defensive QuotaExceededError detection. The real on-device shape is UNKNOWN
 * and this classification is INSTRUMENTATION, not a verified path: engines
 * variously throw a DOMException named 'QuotaExceededError', Firefox reports
 * 'NS_ERROR_DOM_QUOTA_REACHED', and Dexie wraps failures in its own error whose
 * `.inner` carries the original — so the marker can hide in a name, a nested
 * name, a constructor name, or only in message text. Check all of them rather
 * than betting on one. Widen this as real device logs come in.
 */
function isQuotaError(err) {
  const names = [
    err?.name,
    err?.inner?.name,
    err?.inner?.inner?.name,
    err?.constructor?.name,
    err?.inner?.constructor?.name,
  ];
  if (names.some((n) => typeof n === 'string' && n.toLowerCase().includes('quota'))) return true;
  const msg = `${err?.message || ''} ${err?.inner?.message || ''}`.toLowerCase();
  return msg.includes('quota') || msg.includes('storage is full');
}

/**
 * Log the FULL error shape on every failure, quota-classified or not — learning
 * what iOS actually throws is half the point of this instrumentation, and the
 * render site only ever receives a string.
 */
function logCaptureFailure(file, err) {
  console.error('[fieldMedia] capture failed', {
    file: file?.name ?? null,
    fileType: file?.type ?? null,
    fileSize: file?.size ?? null,
    quotaClassified: isQuotaError(err),
    name: err?.name,
    constructorName: err?.constructor?.name,
    message: err?.message,
    inner: err?.inner
      ? {
          name: err.inner.name,
          constructorName: err.inner.constructor?.name,
          message: err.inner.message,
        }
      : null,
    err,
  });
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]); // strip data: prefix
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/** Thumb display source — stamped remote URL wins, local preview last. */
export function mediaThumbSrc(entry) {
  return entry.sas_url || entry.blob_url || entry.object_url || null;
}

export default function FieldMediaGallery({ record, onRecordChange, onOpenViewer, onBufferCapture }) {
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const media = record.media || [];

  const handleFiles = async (fileList) => {
    setError(null);
    setBusy(true);
    const rejected = [];
    const failed = [];
    let quotaHit = false;
    // A record created in the field has tool_id null until its tool_create
    // drains. Queue against the pending id instead, as the create-mode flush
    // does: reconcile re-keys queued items by record_id, so this tool_id is
    // rewritten to the real one when the create lands. A synced record keeps
    // its own tool_id — never its record_id, which is not guaranteed equal.
    const queueToolId = isPendingRecord(record.record_id) ? record.record_id : record.tool_id;
    try {
      for (const file of Array.from(fileList || [])) {
        // Bob's non-image guard: TWO SIGNALS, then a SERVER-PARITY GATE.
        // Signals — iOS Safari sometimes hands over a File with an EMPTY
        // .type, and MIME alone silently dropped those photos (field data
        // loss: the user tapped Camera and nothing happened). Either signal
        // may vouch for the file. The extension signal requires a REAL name
        // extension, because extOf() defaults to 'jpg' when a file has
        // neither name-ext nor known MIME — trusting it unqualified would make
        // the signal unfalsifiable. extOf's fallback stays as-is: it shapes
        // the upload, not this accept decision.
        // Parity gate — the resolved extension must be one upload_media will
        // accept (VALID_EXTS). image/tiff, bmp, gif, avif and svg all satisfy
        // mimeOk but the server rejects them, so accepting on MIME alone
        // queues a photo that can only die in the outbox. Never drop a
        // user-selected file without telling them.
        const mimeOk = (file.type || '').toLowerCase().startsWith('image/');
        const extOk  = (file.name || '').includes('.') && IMAGE_EXTS.has(extOf(file));
        if (!((mimeOk || extOk) && IMAGE_EXTS.has(extOf(file)))) {
          rejected.push(file.name || '(unnamed file)');
          continue;
        }
        // PER-FILE ISOLATION: each file's work gets its own try/catch, so one
        // failure (full device, unreadable file) can neither abort the rest of
        // the batch nor discard the rejection feedback collected above — both
        // of which the single outer try used to do.
        try {
          // Phase 2 — compress at capture. ONE seam: camera and upload, single
          // and multi, and BOTH arms below consume its output. compressImage
          // never throws and never returns null; on any failure it hands back
          // the ORIGINAL bytes, so a photo is never lost to an optimization.
          const { blob, wasCompressed, reason } = await compressImage(file);
          if (!wasCompressed) {
            // CONSOLE-ONLY (D-15). Not every reason here is a failure: `no_gain`
            // means the re-encode simply came out no smaller, which is the
            // correct outcome rather than a fault. Either way there is nothing
            // the rep can act on — the photo queued fine, it is just bigger than
            // it might have been — so surfacing it in the UI would be noise. The
            // signal stays in the log for the Tier 2 chip to read later.
            // Deliberately NOT stamped on the queue item: a field with no reader
            // is exactly what D-12 refused.
            console.warn(`[fieldMedia] original bytes kept (${reason}) — ${Math.round(blob.size / 1024)} KB`);
          }
          // A compressed result is JPEG whatever went in; keeping the original
          // bytes keeps the input's extension. The accept gate above still reads
          // extOf(file) — that decides what we ACCEPT, this decides what we SEND.
          const outExt = wasCompressed ? 'jpg' : extOf(file);
          const base64 = await fileToBase64(blob);
          // 5.3 Phase 3a (D-5.3-P3-1): create-mode buffer arm — no record row
          // exists yet, so NOTHING is enqueued or persisted here; the photo
          // goes to the host's draft-local buffer and flushes at Create.
          if (onBufferCapture) {
            onBufferCapture({
              blob_data: base64,
              byte_size: blob.size, // D-7: real bytes, not base64 length
              file_ext: outExt,
              photo_type: 'tool_photo', // D-5.3-5 — no type prompt
              // Preview from the COMPRESSED blob, never the original file. A
              // thumb rendered from the original would show the browser's
              // EXIF-corrected picture while we ship our decoded pixels — the
              // Phase 3 orientation eyeball would then prove nothing.
              object_url: URL.createObjectURL(blob),
            });
            continue;
          }
          // Enqueue first — outbox_id keys the optimistic entry + reconcile stamp.
          const outboxId = await enqueueMediaAdd({
            record_id: record.record_id,
            tool_id: queueToolId,
            site_id: record.site_id,
            photo_type: 'tool_photo', // D-5.3-5 — no type prompt
            blob_data: base64,
            byte_size: blob.size, // D-7: real bytes, not base64 length
            file_ext: outExt,
          });
          // Same reason as the buffer arm: preview the bytes we are shipping.
          const objectUrl = URL.createObjectURL(blob);
          await ensureOpen();
          const row = await fieldDb.records.get(record.record_id);
          if (!row) throw new Error('record row missing');
          row.media = [
            ...(row.media || []),
            {
              outbox_id: outboxId,
              pending: true,
              object_url: objectUrl,
              photo_type: 'tool_photo',
              file_ext: outExt,
            },
          ];
          await fieldDb.records.put(row);
          onRecordChange?.(row);
        } catch (err) {
          logCaptureFailure(file, err);
          if (isQuotaError(err)) quotaHit = true;
          failed.push(file.name || '(unnamed file)');
        }
      }
      // ONE report after the loop. Rejections now survive a mid-batch failure;
      // the quota copy names storage as the cause and gives the rep the action.
      const parts = [];
      if (quotaHit) {
        parts.push('Device storage is full — sync now, then capture again.');
      } else if (failed.length) {
        parts.push('Photo could not be queued — try again');
      }
      if (rejected.length) {
        parts.push(
          rejected.length === 1
            ? `Not a supported image — skipped ${rejected[0]}`
            : `Not supported images — skipped ${rejected.join(', ')}`
        );
      }
      if (parts.length) setError(parts.join(' '));
    } catch (err) {
      // Backstop only: per-file work is inner-caught, so this is reachable
      // just for a failure outside the loop body.
      logCaptureFailure(null, err);
      setError('Photo could not be queued — try again');
    } finally {
      setBusy(false);
    }
  };

  const onInputChange = (e) => {
    // Materialize the LIVE FileList into a real array BEFORE resetting the
    // input — `value = ''` empties input.files, and a bare reference to it
    // goes with it. This exact ordering was the Phase 2 dead-capture bug:
    // the handler fired but iterated an already-emptied list, silently.
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // re-selecting the same file must re-fire
    handleFiles(files);
  };

  return (
    <div className="fmg" data-testid="field-media-gallery">
      <div className="fmg-actions">
        <button
          type="button"
          className="fmg-btn"
          data-testid="fmg-camera-btn"
          onClick={() => cameraRef.current?.click()}
          disabled={busy}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M5 7h1a2 2 0 0 0 2-2a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1a2 2 0 0 0 2 2h1a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-14a2 2 0 0 1-2-2v-9a2 2 0 0 1 2-2" />
            <path d="M9 13a3 3 0 1 0 6 0a3 3 0 0 0-6 0" />
          </svg>
          Camera
        </button>
        <button
          type="button"
          className="fmg-btn"
          data-testid="fmg-upload-btn"
          onClick={() => uploadRef.current?.click()}
          disabled={busy}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
            <path d="M7 9l5-5l5 5" />
            <path d="M12 4l0 12" />
          </svg>
          Upload
        </button>
      </div>

      {media.length === 0 ? (
        <p className="fmg-empty" data-testid="fmg-empty">
          No photos yet — tap Camera or Upload to add
        </p>
      ) : (
        <div className="fmg-grid" data-testid="fmg-grid">
          {media.map((entry, idx) => {
            const src = mediaThumbSrc(entry);
            return (
              <button
                key={entry.outbox_id ?? entry.media_id ?? entry.buffer_id ?? idx}
                type="button"
                className="fmg-thumb"
                onClick={() => onOpenViewer(idx)}
                aria-label={`Open photo ${idx + 1}`}
              >
                {src
                  ? <img className="fmg-thumb-img" src={src} alt="" loading="lazy" />
                  : <span className="fmg-thumb-missing">?</span>}
                {entry.pending && (
                  <span className="fmg-pending-chip" data-testid="fmg-pending-chip">PENDING</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {error && <p className="fmg-error" data-testid="fmg-error">{error}</p>}

      {/* Hidden inputs — Bob's fc2/fg2 pattern */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={onInputChange}
        data-testid="fmg-camera-input"
      />
      <input
        ref={uploadRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={onInputChange}
        data-testid="fmg-upload-input"
      />
    </div>
  );
}
